import { ReturnsAndClaimsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import FlipBackwardIcon from "@redotech/redo-icons/flip-backward.svg";
import FlipBackwardFilledIcon from "@redotech/redo-icons/flip-backward_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import { useNavTreeNode } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { OrdersReviewCountContext } from "../../../../services/orders/orders-review-count-service";
import { ClaimsItem } from "../orders/claims";
import { FraudItem } from "../orders/fraud";
import { RecycleItem } from "../orders/recycle";
import { ReturnsItem } from "../orders/returns";
import { WarrantiesItem } from "../orders/warranties";
import { WithdrawalItem } from "../orders/withdrawal";
import { ManagedReturnsItem } from "./managed-returns";
import { ReturnsAnalyticsItem } from "./returns-analytics";

export function useReturnsNavigation(): PageNavigationEntry | null {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const pathRoot = routes.returnsBaseUrl;

  const { claimsNeedingReview, returnsNeedingReview, warrantiesNeedingReview } =
    useRequiredContext(OrdersReviewCountContext);

  const canViewReturnsSection = useHasPermission(
    ReturnsAndClaimsPermissions.VIEW_RETURN,
    ReturnsAndClaimsPermissions.MANAGE_WARRANTY,
    ReturnsAndClaimsPermissions.MANAGE_RECYCLING_REQUESTS,
    ReturnsAndClaimsPermissions.MANAGE_WITHDRAWAL_REQUESTS,
    ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
  );

  if (!canViewReturnsSection) {
    return null;
  }

  const hasNotification =
    !!claimsNeedingReview ||
    !!returnsNeedingReview ||
    !!warrantiesNeedingReview;

  const primaryTab: LinkPrimaryTab = {
    id: "returns",
    hasNotification,
    UnfilledIcon: FlipBackwardIcon,
    FilledIcon: FlipBackwardFilledIcon,
    type: "link",
    pathRoot,
    href: routes.returnsDefaultRoute,
    friendlyName: t("Returns"),
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "returns",
    name: t("Returns & claims"),
    NavContent: ReturnsSecondaryNavigation,
  };

  return { primaryTab, secondaryNavigationData, isActiveProduct: true };
}

const ReturnsSecondaryNavigation = memo(function ReturnsSecondaryNavigation() {
  const { t } = useTranslation();
  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: t("Returns & claims"),
  });

  return (
    <TreeContext.Provider value={nodeId}>
      <ReturnsAnalyticsItem />
      <ClaimsItem />
      <FraudItem />
      <ManagedReturnsItem />
      <RecycleItem />
      <ReturnsItem />
      <WarrantiesItem />
      <WithdrawalItem />
    </TreeContext.Provider>
  );
});
